import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import AddIcon from "@mui/icons-material/Add";
import DateRangeIcon from "@mui/icons-material/DateRange";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu as MuiMenu,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "components/popups";
import { BalanceApproval } from "components/Prompts";
import { PaymentStatus } from "constants/debt";
import dayjs from "dayjs";
import { useModals, usePrompt } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealIdSelector, useReportsQueriesSelector } from "store";
import { useDebtActions, useUserActions } from "store/actions-context";
import { AmortizationRow, DebtSegment, FacilityFee } from "store/types";
import { getFacilitySegment, getLastSegmentsByDealId, getWithdrawalSegments } from "utils/debt";

import { AddValueDrawer } from "./AddValueDrawer";
import { AdjustmentDrawer } from "./AdjustmentDrawer";
import { AmortizationDateRangeFilterPopover } from "./AmortizationDateRangeFilterPopover";
import { AmortizationDrawer } from "./AmortizationDrawer";
import { AmortizationSummaryCard } from "./AmortizationSummaryCard";
import { AmortizationTable } from "./AmortizationTable";
import { FacilityCreationWizard } from "./FacilityCreationWizard";
import { FacilityTermsDrawer } from "./FacilityTermsDrawer";
import { PaymentConfirmationDrawer } from "./PaymentConfirmationDrawer";
import { PrepaymentDrawer } from "./PrepaymentDrawer";

const today = new Date().toISOString().split("T")[0];

export const AmortizationPage: React.FC = () => {
  const { t, i18n } = useTranslation("debt");
  const dealId = useDealIdSelector() ?? 0; // This page exist only in deal page context.
  const { updateAmortizationTableColumnsAction } = useUserActions();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFullDrawerOpen, setIsFullDrawerOpen] = useState(false);
  const [editMenuAnchor, setEditMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedExplanation, setSelectedExplanation] = useState<string | null>(null);
  const handleExplanationSelect = useCallback((exp: string | null) => setSelectedExplanation(exp ?? null), []);
  const tableCardRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<[number, number | undefined] | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  const [isPrepaymentDrawerOpen, setIsPrepaymentDrawerOpen] = useState(false);
  const [isAdjustmentDrawerOpen, setIsAdjustmentDrawerOpen] = useState(false);
  const [isPaymentConfirmationDrawerOpen, setIsPaymentConfirmationDrawerOpen] = useState(false);
  const [isAddValueDrawerOpen, setIsAddValueDrawerOpen] = useState(false);
  const [paymentConfirmationMode, setPaymentConfirmationMode] = useState<"approve" | "diff">("approve");
  const [openWithdrawalTab, setOpenWithdrawalTab] = useState(false);
  const [splitLinkage, setSplitLinkage] = useState(false);
  const [dateFilterAnchor, setDateFilterAnchor] = useState<null | HTMLElement>(null);
  const [dateFilterFrom, setDateFilterFrom] = useState<string | null>(null);
  const [dateFilterTo, setDateFilterTo] = useState<string | null>(null);
  const [hedgingDate, setHedgingDate] = useState<string | null>(dayjs().format("YYYY-MM-DD"));
  const { currency, exchangeMethod } = useReportsQueriesSelector();
  const {
    getAmortizationTableAction,
    removeCustomPaymentAction,
    updateBulkDebtSegmentsAction,
    getBalanceApprovalAction,
    downloadAmortizationTableAction,
    createWithdrawalAction
  } = useDebtActions();

  const { errorModal, deleteModal } = useModals();
  const openPrompt = usePrompt();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["amortizationTable", dealId, today, splitLinkage, currency, exchangeMethod, hedgingDate, i18n.language],
    queryFn: () =>
      getAmortizationTableAction(dealId, today, {
        split_linkage: splitLinkage,
        currency,
        exchange_date: exchangeMethod,
        to_date: hedgingDate || undefined
      })
  });

  useEffect(() => {
    const cols = data?.table?.columns;
    if (cols?.length) {
      setColumnVisibility((prev) => {
        const next = { ...prev };
        cols.forEach((col: { key: string; visible: boolean }) => {
          if (!(col.key in next)) next[col.key] = col.visible;
        });
        return next;
      });
    }
  }, [data?.table?.columns]);

  const { action: getBalanceApproval, loading: getBalanceApprovalLoading } = useActionHook(getBalanceApprovalAction);
  const { action: createWithdrawal, loading: createWithdrawalLoading } = useActionHook(createWithdrawalAction);
  const { action: removeCustomPayment } = useActionHook(removeCustomPaymentAction);
  const { action: updateBulkAction, loading: updateBulkLoading } = useActionHook(updateBulkDebtSegmentsAction);
  const { action: downloadAmortizationTable, loading: downloadAmortizationTableLoading } = useActionHook(
    downloadAmortizationTableAction,
    {
      onReject: (e) => {
        errorModal({ msg: e.message });
      }
    }
  );

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, index: number, childIndex?: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex([index, childIndex]);
  }, []);

  const handleClose = (shuoldRefetch?: boolean) => {
    setIsDrawerOpen(false);
    setIsFullDrawerOpen(false);
    setSelectedRowIndex(null);
    setOpenWithdrawalTab(false);

    if (shuoldRefetch) {
      refetch();
    }
  };

  const handleSplitLinkageToggle = () => {
    setSplitLinkage((prev) => !prev);
  };

  const handleChangeValue = () => {
    setAnchorEl(null);
    if (selectedRowIndex !== null) {
      setIsDrawerOpen(true);
    }
  };

  const handlePrepayment = () => {
    setAnchorEl(null);
    if (selectedRowIndex !== null) {
      setIsPrepaymentDrawerOpen(true);
    }
  };

  const handleAdjustment = () => {
    setAnchorEl(null);
    if (selectedRowIndex !== null) {
      setIsAdjustmentDrawerOpen(true);
    }
  };

  // const handleAddValue = () => {
  //   setAnchorEl(null);
  //   if (selectedRowIndex !== null) {
  //     setIsAddValueDrawerOpen(true);
  //   }
  // };

  const handleConfirmPayment = () => {
    setAnchorEl(null);
    if (selectedRowIndex !== null) {
      setPaymentConfirmationMode("approve");
      setIsPaymentConfirmationDrawerOpen(true);
    }
  };

  const handleActualPayment = () => {
    setAnchorEl(null);
    if (selectedRowIndex !== null) {
      setPaymentConfirmationMode("diff");
      setIsPaymentConfirmationDrawerOpen(true);
    }
  };

  const handleRemovePrepayment = () => {
    if (selectedRowIndex !== null) {
      const row = getRow(selectedRowIndex[0], selectedRowIndex[1]);
      const date = row.date;
      deleteModal({
        action: async () => {
          await removeCustomPayment({ dealId: row.deal_id || dealId, date });
          refetch();
        },
        element: t("amortization.removePrepayment"),
        message: t("amortization.removePrepaymentMessage")
      });
    }
    setAnchorEl(null);
  };

  const handleBalanceApproval = async () => {
    const res = await openPrompt({
      Component: BalanceApproval
    });

    if (!res) return;

    try {
      await getBalanceApproval({
        dealId,
        type: res.balance.toLowerCase(),
        ...(res.date && { report_date: res.date.toISOString() })
      });
    } catch (error: any) {
      toast.error(error?.message ?? "An error occurred");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{t("amortization.loadError")}</Typography>
      </Box>
    );
  }

  const wizardOnApply = async (segments: DebtSegment[], targetDealId?: number, fees?: FacilityFee[]) => {
    const result = await updateBulkAction({
      dealId: targetDealId || dealId,
      segments,
      fees
    });
    // refetch();
    return result;
  };

  if (!data?.segments.length) {
    return (
      <>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            height: "100%",
            minHeight: "50vh"
          }}
        >
          <ViewWeekOutlinedIcon sx={{ fontSize: 48, color: "text.secondary" }} />
          <Typography color="text.secondary">{t("amortization.noTable")}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsDrawerOpen(true)}>
            {t("amortization.addTerms")}
          </Button>
        </Box>
        <FacilityCreationWizard
          open={isDrawerOpen}
          onClose={handleClose}
          onApply={wizardOnApply}
          onCreateWithdrawal={(segment) => createWithdrawal(dealId, segment)}
          loading={updateBulkLoading}
        />
      </>
    );
  }

  const { table, segments, custom_payments, adjustments = [] } = data;

  const lastSegments = getLastSegmentsByDealId(segments);
  const facilitySegment = getFacilitySegment(lastSegments);
  const withdrawalSegmentsLast = getWithdrawalSegments(lastSegments);
  const isFacility = !!facilitySegment;

  // Get ALL segments for facility and withdrawals (not just last)
  const facilityDealId = facilitySegment?.deal_id;
  const facilitySegments = facilityDealId ? segments.filter((s) => s.deal_id === facilityDealId) : [];
  const withdrawalSegments = segments.filter((s) => s.is_withdrawal && !s.is_facility);
  const toComparableRowDate = (dateStr: string | undefined) => {
    if (!dateStr) return "";
    const parsed = dayjs(dateStr);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : (dateStr.split("T")[0] ?? "");
  };
  const filteredRows = table.rows.filter((row) => {
    const rowDate = toComparableRowDate(row.date);
    if (!rowDate) return false;
    if (dateFilterFrom && rowDate < dateFilterFrom) return false;
    if (dateFilterTo && rowDate > dateFilterTo) return false;
    return true;
  });

  const showAddWithdrawalButton =
    isFacility && !withdrawalSegmentsLast.length && !table.rows.filter((row) => !row.is_report).length;

  const getRow = (index: number, childIndex?: number): AmortizationRow => {
    return typeof childIndex === "number" ? filteredRows[index].children![childIndex] : filteredRows[index];
  };

  const getDealId = (index: number, childIndex?: number) => {
    const row = getRow(index, childIndex);
    if (row.children && dealId) {
      return dealId;
    }
    return segments.find((segment) => row.segmentIds.includes(segment.id))?.deal_id;
  };

  const selectedRow = selectedRowIndex ? getRow(selectedRowIndex[0], selectedRowIndex[1]) : null;
  const selectedDealId = selectedRowIndex ? getDealId(selectedRowIndex?.[0], selectedRowIndex?.[1]) : dealId;
  const isChildless = !selectedRow?.children;
  const isParent = selectedRowIndex?.[1] === undefined;
  const selectedDealSegments = segments.filter((segment) => segment.deal_id === selectedDealId);
  const selectedRowSegments = selectedRow
    ? selectedRow.segmentIds.map((id) => segments.find((segment) => segment.id === id)!)
    : [];
  const editable = isParent || selectedRowSegments.every((segment) => segment.overriding_parent);

  const selectedPrepayment = custom_payments.find(
    (payment) => payment.date === selectedRow?.date && payment.deal_id === selectedDealId && !payment.is_actual
  );
  const selectedActualPayment = custom_payments.find(
    (payment) => payment.date === selectedRow?.date && payment.deal_id === selectedDealId && payment.is_actual
  );
  const selectedAdjustment = adjustments.find(
    (adjustment) => adjustment.date === selectedRow?.date && adjustment.deal_id === selectedDealId
  );

  return (
    <Stack ref={scrollRef} gap={3} height="100%" overflow="auto">
      <AmortizationSummaryCard
        data={data}
        isFacility={isFacility}
        onEditClick={(e) => setEditMenuAnchor(e.currentTarget)}
        scrollContainerRef={scrollRef}
      />

      {showAddWithdrawalButton && (
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            height: "100%",
            minHeight: "50vh"
          }}
        >
          <ViewWeekOutlinedIcon sx={{ fontSize: 48, color: "text.secondary" }} />
          <Typography color="text.secondary">{t("amortization.noWithdrawals")}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpenWithdrawalTab(true);
              setIsDrawerOpen(true);
            }}
          >
            {t("amortization.createWithdrawal")}
          </Button>
        </Box>
      )}

      {/* Table Card */}
      {!showAddWithdrawalButton && (
        <Card
          ref={tableCardRef}
          sx={{
            p: 5,
            display: "flex",
            flexDirection: "column",
            overflow: "unset",
            flexGrow: 1,
            height: 0,
            minHeight: 300
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">{t("amortization.tableTitle")}</Typography>
            {selectedExplanation && (
              <Typography sx={{ fontSize: "13px", color: "text.secondary", fontFamily: "monospace", mx: 2, flex: 1 }}>
                {selectedExplanation}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={<Switch size="small" checked={splitLinkage} onChange={handleSplitLinkageToggle} />}
                label={t("amortization.separateIndexation")}
                labelPlacement="start"
                sx={{ mr: 1 }}
              />
              <IconButton size="sm" onClick={(e) => setColumnsMenuAnchor(e.currentTarget)} title="Show/hide columns">
                <ViewWeekOutlinedIcon />
              </IconButton>
              <MuiMenu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {table.columns.map((col) => (
                  <MenuItem
                    key={col.key}
                    onClick={() => {
                      const next = { ...columnVisibility, [col.key]: !columnVisibility[col.key] };
                      setColumnVisibility(next);
                      updateAmortizationTableColumnsAction(next);
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox size="small" checked={columnVisibility[col.key] !== false} disableRipple />
                    </ListItemIcon>
                    <ListItemText primary={col.label} />
                  </MenuItem>
                ))}
              </MuiMenu>
              <IconButton size="sm" title="Filter by date range" onClick={(e) => setDateFilterAnchor(e.currentTarget)}>
                <DateRangeIcon />
              </IconButton>
              <AmortizationDateRangeFilterPopover
                anchorEl={dateFilterAnchor}
                onClose={() => setDateFilterAnchor(null)}
                appliedFrom={dateFilterFrom}
                appliedTo={dateFilterTo}
                appliedHedgingDate={hedgingDate}
                onApply={(from, to, newHedgingDate) => {
                  setDateFilterFrom(from);
                  setDateFilterTo(to);
                  setHedgingDate(newHedgingDate);
                }}
                onClear={() => {
                  setDateFilterFrom(null);
                  setDateFilterTo(null);
                  setHedgingDate(dayjs().format("YYYY-MM-DD"));
                }}
              />
              {downloadAmortizationTableLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <IconButton
                    size="sm"
                    onClick={() => {
                      downloadAmortizationTable({
                        dealId,
                        split_linkage: splitLinkage,
                        date: today,
                        from_date: dateFilterFrom || undefined,
                        to_date: hedgingDate || undefined
                      });
                    }}
                    disabled={downloadAmortizationTableLoading}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </>
              )}
              {getBalanceApprovalLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Menu
                  iconButtonProps={{ size: "sm" }}
                  options={[
                    { title: t("amortization.balanceConfirmation"), onClick: handleBalanceApproval },
                    { title: t("amortization.paymentRequest") }
                  ]}
                />
              )}
            </Box>
          </Box>

          <AmortizationTable
            table={table}
            rows={filteredRows}
            onMenuOpen={handleMenuOpen}
            onChildMenuOpen={handleMenuOpen}
            onExplanationSelect={handleExplanationSelect}
            boundaryRef={tableCardRef}
            columnVisibility={columnVisibility}
          />
        </Card>
      )}

      <MuiMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedRowIndex(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <MenuItem onClick={handleChangeValue} disabled={!editable}>
          {t("amortization.changeValue")}
        </MenuItem>
        {isChildless && !!selectedPrepayment && (
          <MenuItem onClick={handleRemovePrepayment}>{t("amortization.removePrepayment")}</MenuItem>
        )}
        {isChildless && (
          <MenuItem onClick={handlePrepayment}>
            {selectedPrepayment ? t("amortization.editPrepayments") : t("amortization.prepayment")}
          </MenuItem>
        )}
        {isChildless && (
          <MenuItem onClick={handleAdjustment}>
            {selectedAdjustment ? t("amortization.editAdjustment") : t("amortization.adjustment")}
          </MenuItem>
        )}
        {isChildless && selectedRow?.status !== PaymentStatus.RESOLVED && (
          <MenuItem onClick={handleConfirmPayment}>{t("amortization.confirmPayment")}</MenuItem>
        )}
        {isChildless && selectedRow?.status !== PaymentStatus.RESOLVED && (
          <MenuItem onClick={handleActualPayment}>{t("amortization.applyChangedPayment")}</MenuItem>
        )}
      </MuiMenu>

      <MuiMenu anchorEl={editMenuAnchor} open={Boolean(editMenuAnchor)} onClose={() => setEditMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setEditMenuAnchor(null);
            setIsDrawerOpen(true);
          }}
        >
          {t("amortization.editWithWizard")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEditMenuAnchor(null);
            setIsFullDrawerOpen(true);
          }}
        >
          {t("amortization.editAllFields")}
        </MenuItem>
      </MuiMenu>

      <FacilityCreationWizard
        open={isDrawerOpen}
        editMode
        existingSegments={isFacility ? facilitySegments : selectedDealSegments}
        existingFees={data.fees}
        existingWithdrawals={withdrawalSegments}
        onCreateWithdrawal={(segment) => createWithdrawal(dealId, segment)}
        onClose={handleClose}
        onApply={async (segments, targetDealId, fees) => {
          const dealIdToUse = targetDealId || (isFacility ? facilityDealId : selectedDealId) || dealId;

          // When targetDealId is a withdrawal deal, include all its segments so the
          // server doesn't delete the ones not in the request.
          let segmentsToSend = segments;
          if (targetDealId) {
            const allForDeal = data.segments.filter((s) => s.deal_id === targetDealId);
            const incomingById = new Map(segments.map((s) => [s.id, s]));
            segmentsToSend = allForDeal.map((s) => incomingById.get(s.id) ?? s);
          }

          await updateBulkAction({ dealId: dealIdToUse, segments: segmentsToSend, fees });
          refetch();
        }}
        loading={updateBulkLoading}
      />

      <FacilityTermsDrawer
        open={isFullDrawerOpen && isFacility}
        onClose={handleClose}
        facilitySegments={facilitySegments}
        fees={data.fees}
        withdrawalSegments={withdrawalSegments}
        openWithdrawalTab={openWithdrawalTab}
        onApply={async (segments, targetDealId, fees) => {
          try {
            const isNewWithdrawal =
              segments.length === 1 &&
              !segments[0].id &&
              segments[0].is_withdrawal &&
              !segments[0].is_facility &&
              targetDealId !== dealId;
            const response = isNewWithdrawal
              ? await createWithdrawal(dealId, segments[0] as any)
              : await updateBulkAction({ dealId: targetDealId || dealId, segments, fees });
            refetch();
            return response;
          } catch (error) {
            throw error;
          }
        }}
        loading={updateBulkLoading || createWithdrawalLoading}
      />

      <AmortizationDrawer
        open={isFullDrawerOpen && !isFacility}
        segments={selectedDealSegments}
        newSegmentDate={selectedRow?.date}
        newSegmentId={Number(selectedRow?.segmentIds[0])}
        loading={updateBulkLoading}
        onClose={handleClose}
        onApply={async (segments) => {
          await updateBulkAction({ dealId: selectedDealId!, segments });
          refetch();
          handleClose();
        }}
      />
      <PrepaymentDrawer
        open={isPrepaymentDrawerOpen}
        amortizationRow={selectedRow}
        prepayment={selectedPrepayment}
        dealId={selectedDealId!}
        onClose={(updated) => {
          setIsPrepaymentDrawerOpen(false);
          if (updated) {
            refetch();
          }
          setSelectedRowIndex(null);
        }}
      />
      <AdjustmentDrawer
        open={isAdjustmentDrawerOpen}
        amortizationRow={selectedRow}
        adjustment={selectedAdjustment}
        dealId={selectedDealId!}
        onClose={(updated) => {
          setIsAdjustmentDrawerOpen(false);
          if (updated) {
            refetch();
          }
          setSelectedRowIndex(null);
        }}
      />
      <AddValueDrawer
        open={isAddValueDrawerOpen}
        rows={selectedRow ? selectedRow.children || [selectedRow] : undefined}
        segments={selectedRowSegments}
        onClose={() => {
          setIsAddValueDrawerOpen(false);
          setSelectedRowIndex(null);
        }}
      />
      <PaymentConfirmationDrawer
        open={isPaymentConfirmationDrawerOpen}
        amortizationRow={selectedRow}
        dealId={selectedDealId!}
        columns={table.columns}
        mode={paymentConfirmationMode}
        actualPayment={selectedActualPayment}
        prepayment={selectedPrepayment}
        onClose={(updated) => {
          setIsPaymentConfirmationDrawerOpen(false);
          if (updated) {
            refetch();
          }
          setSelectedRowIndex(null);
        }}
      />
    </Stack>
  );
};
